/**
 * MarkdownTable — the in-thread table block.
 *
 * Tables can't be opened in a separate view yet, so the in-thread one has to
 * be genuinely good: real `<table>` semantics, a hairline grid, mono
 * right-aligned numerics, status pills, and a pinned first column so a
 * 6-column table stays usable in a 440px panel.
 *
 * The CELLS are still rendered by ReactMarkdown (this component renders
 * `children`), so a link, bold run, inline code or citation marker inside a
 * cell keeps working. The hast `node` is read only to DECIDE presentation —
 * which columns are numeric — and to build the copy/download payloads (the
 * copy carries an HTML table too, so links survive a paste into Docs or Word).
 * Rendering from the flattened node instead was a silent-data-loss bug: a cell
 * containing `[Buffer prep](/notes/abc)` lost its href with no error.
 *
 * Column alignment is applied with nth-of-type rules rather than per-cell
 * props, because the cells are ReactMarkdown's elements and don't know their
 * own index.
 */

import { Box, HStack, IconButton, Text } from '@chakra-ui/react';
import type { Element } from 'hast';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useToast } from '../../../hooks/ui/useToast';
import { ANALYTICS_EVENTS } from '../../../constants/analytics';
import { safeAnalytics, trackClipboardCopyFailed } from '../../../utils/analytics';
import { MaterialIcon } from '../../ui/MaterialIcon';
import { Tooltip } from '../../ui/Tooltip';
import { parseTable, summarise, toCsv, toHtml, toTsv, type TableData } from './markdownTable.utils';

/** Caption bar: how big this table is, and what you can do with it. */
function TableCaption({
  data,
  onCopy,
  onDownload,
}: {
  readonly data: TableData;
  readonly onCopy: () => void;
  readonly onDownload: () => void;
}) {
  return (
    // No tint: the block is one surface separated by hairlines, per "hairline
    // grid, not heavy boxes". It also keeps text.muted on bg.surface, which
    // clears AA in both modes — on bg.subtle it lands at 4.13:1 and fails.
    <HStack
      justify="space-between"
      px={2}
      py={1}
      borderBottom="1px solid"
      borderColor="border.muted"
    >
      <Text fontSize="2xs" color="text.muted" fontWeight="medium">
        {summarise(data)}
      </Text>
      <HStack spacing={0}>
        <Tooltip label="Copy table" placement="top">
          <IconButton
            aria-label="Copy table"
            icon={<MaterialIcon name="content_copy" boxSize={3.5} />}
            size="xs"
            variant="ghost"
            color="text.muted"
            onClick={onCopy}
            _hover={{ bg: 'bg.hover', color: 'text.primary' }}
          />
        </Tooltip>
        <Tooltip label="Download CSV" placement="top">
          <IconButton
            aria-label="Download table as CSV"
            icon={<MaterialIcon name="download" boxSize={3.5} />}
            size="xs"
            variant="ghost"
            color="text.muted"
            onClick={onDownload}
            _hover={{ bg: 'bg.hover', color: 'text.primary' }}
          />
        </Tooltip>
      </HStack>
    </HStack>
  );
}

/**
 * Per-column presentation, expressed as nth-of-type rules. Numeric columns are
 * right-aligned and mono; the first column is pinned so it survives horizontal
 * scroll (its own background stops content passing through it).
 */
function columnStyles(numericColumns: ReadonlySet<number>) {
  const rules: Record<string, unknown> = {
    'th:first-of-type, td:first-of-type': {
      position: 'sticky',
      left: 0,
      zIndex: 1,
      bg: 'bg.surface',
      fontWeight: 'medium',
    },
  };
  for (const col of numericColumns) {
    rules[`th:nth-of-type(${col + 1}), td:nth-of-type(${col + 1})`] = {
      textAlign: 'right',
      fontFamily: 'mono',
    };
  }
  return rules;
}

export function MarkdownTable({
  node,
  children,
}: {
  readonly node?: Element;
  readonly children?: React.ReactNode;
}) {
  const toast = useToast();
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [hasOverflow, setHasOverflow] = useState(false);

  const data = useMemo(() => (node ? parseTable(node) : null), [node]);

  // Scroll hint + keyboard reachability only when there's actually something
  // to scroll to. `measure()` runs BEFORE the ResizeObserver guard so an
  // environment without ResizeObserver still gets a correct first answer —
  // failing closed here would hide the off-screen columns from keyboard users
  // with no indication they exist.
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const measure = () => setHasOverflow(el.scrollWidth > el.clientWidth + 1);
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [data]);

  const handleCopy = useCallback(() => {
    if (!data || !node) return;
    // navigator.clipboard is undefined outside a secure context (a dev server
    // reached over a LAN IP, say), where `.writeText(...)` throws
    // SYNCHRONOUSLY — past the .catch below, so the error toast never fires.
    // An optional chain would only trade that throw for silence: the whole
    // chain, .catch() included, short-circuits. Branch explicitly so the user
    // hears something either way.
    if (!navigator.clipboard) {
      trackClipboardCopyFailed('bird_table', 'unavailable');
      toast({ title: "Couldn't copy the table", status: 'error' });
      return;
    }
    // Both flavours: rich-text targets take the HTML table, links included,
    // and anything plain-text takes the TSV. writeText alone made every paste
    // into Docs or Word a run of tab-separated text with the links gone.
    // Where ClipboardItem is missing, or the rich write is refused, the TSV
    // alone is still better than the error toast.
    const clipboard = navigator.clipboard;
    const tsv = toTsv(data);
    const writeTsv = () => clipboard.writeText(tsv).then(() => 'tsv' as const);
    const written =
      typeof clipboard.write === 'function' && typeof ClipboardItem !== 'undefined'
        ? clipboard
            .write([
              new ClipboardItem({
                'text/plain': new Blob([tsv], { type: 'text/plain' }),
                'text/html': new Blob([toHtml(node, window.location.origin)], {
                  type: 'text/html',
                }),
              }),
            ])
            .then(() => 'html_tsv' as const, writeTsv)
        : writeTsv();
    void written
      .then((format) => {
        safeAnalytics.track(ANALYTICS_EVENTS.CHAT_TABLE_EXPORTED, {
          format,
          row_count: data.rows.length,
          column_count: data.headers.length,
        });
        toast({ title: 'Table copied', status: 'success' });
      })
      .catch(() => {
        trackClipboardCopyFailed('bird_table', 'rejected');
        toast({ title: "Couldn't copy the table", status: 'error' });
      });
  }, [data, node, toast]);

  const handleDownload = useCallback(() => {
    if (!data) return;
    const blob = new Blob([toCsv(data)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    // Document-connected anchor + deferred revoke — the convention every other
    // download site in this package uses (exportUtils, Audit, SecurityTab).
    // A detached anchor with a synchronous revoke silently no-ops in some
    // browsers, which is the worst kind of download bug.
    const link = document.createElement('a');
    link.href = url;
    link.download = 'bower-table.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    safeAnalytics.track(ANALYTICS_EVENTS.CHAT_TABLE_EXPORTED, {
      format: 'csv',
      row_count: data.rows.length,
      column_count: data.headers.length,
    });
  }, [data]);

  if (!data || data.headers.length === 0) return null;

  return (
    <Box
      w="full"
      minW={0}
      mb={3}
      border="1px solid"
      borderColor="border.muted"
      borderRadius="10px"
      overflow="hidden"
      bg="bg.surface"
    >
      <TableCaption data={data} onCopy={handleCopy} onDownload={handleDownload} />

      {/* A scrollable region has to be reachable by keyboard (WCAG 2.1.1) —
          without tabIndex a keyboard-only user can never see the columns past
          the right edge. Focusable only when it actually overflows, so a table
          that fits adds no dead tab stop. The focus ring is INSET: the wrapper
          is overflow:hidden for the radius, which would clip an outer ring. */}
      <Box
        ref={scrollerRef}
        overflowX="auto"
        tabIndex={hasOverflow ? 0 : undefined}
        role={hasOverflow ? 'region' : undefined}
        aria-label={hasOverflow ? 'Table, scrollable' : undefined}
        _focusVisible={{
          outline: 'none',
          boxShadow:
            'inset 0 0 0 var(--chakra-borderWidths-interactive) var(--chakra-colors-border-focus)',
        }}
      >
        <Box
          as="table"
          w="full"
          fontSize="xs"
          sx={{
            borderCollapse: 'separate',
            borderSpacing: 0,
            'th, td': {
              px: 2,
              py: 1.5,
              borderBottom: '1px solid',
              borderColor: 'border.muted',
              whiteSpace: 'nowrap',
              textAlign: 'left',
              color: 'text.primary',
            },
            th: {
              // Weight comes from the semibold and the hairline under it, not
              // a fill. bg.surface (not bg.subtle) is also what keeps
              // text.muted above 4.5:1. Never `textTransform: 'uppercase'`:
              // headers carry units, and uppercase makes `µL` read as `ML`
              // (millilitres), `mM` as `MM` and `pH` as `PH`.
              bg: 'bg.surface',
              color: 'text.muted',
              fontSize: 'xs',
              fontWeight: 'semibold',
            },
            'tbody tr:last-of-type td': { borderBottom: 'none' },
            ...columnStyles(data.numericColumns),
          }}
        >
          {children}
        </Box>
      </Box>

      {hasOverflow && (
        // aria-hidden: the scroll region above already announces itself as
        // scrollable; repeating it here is noise on the way out of the table.
        <HStack
          spacing={1}
          px={2}
          py={1}
          borderTop="1px solid"
          borderColor="border.muted"
          aria-hidden
        >
          <MaterialIcon name="keyboard_double_arrow_right" boxSize={3.5} color="text.muted" />
          <Text fontSize="2xs" color="text.muted">
            Scroll to see more columns
          </Text>
        </HStack>
      )}
    </Box>
  );
}
